// Sidebar structure for the admin dashboard.
// Each item's `roles` array controls which roles can see it.
// Special role `*` means all staff (any authenticated non-customer).
//
// The sidebar mirrors the modules requested by product:
// Dashboard, House Plans, Orders, Customers, Enquiries, Team Management,
// Content Management, Media Library, Settings, Reports, Backup & Maintenance.

export const ADMIN_NAV = [
  {
    group: "Dashboard",
    icon: "LayoutDashboard",
    roles: ["*"],
    items: [
      { label: "Overview", to: "/admin/dashboard", roles: ["*"] },
    ],
  },
  {
    group: "House Plans",
    icon: "Home",
    roles: ["super_admin", "admin", "team_manager"],
    items: [
      { label: "All Plans", to: "/admin/plans", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Add New Plan", to: "/admin/plans/new", roles: ["super_admin", "admin", "team_manager"] },
      // Iter60A — Floor Plans is a specialized workflow on top of the SAME Plan
      // engine. The list pre-filters `sales_channel in {floor_plan, both}` and
      // "+ New Floor Plan" opens AdminPlanForm with channel preselected.
      { label: "Floor Plans", to: "/admin/plans/floor", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Draft Plans", to: "/admin/plans?status=draft", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Published Plans", to: "/admin/plans?status=published", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Archived Plans", to: "/admin/plans?status=archived", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Categories", to: "/admin/categories", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Collections", to: "/admin/collections", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Styles", to: "/admin/styles", roles: ["super_admin", "admin", "team_manager"] },
    ],
  },
  {
    group: "Orders",
    icon: "ShoppingBag",
    roles: ["super_admin", "admin", "sales"],
    items: [
      { label: "Orders", to: "/admin/orders", roles: ["super_admin", "admin", "sales"] },
      { label: "Payments", to: "/admin/payments", roles: ["super_admin", "admin"] },
      { label: "Refunds", to: "/admin/refunds", roles: ["super_admin", "admin"] },
      { label: "Download History", to: "/admin/downloads", roles: ["super_admin", "admin"] },
    ],
  },
  {
    group: "Customers",
    icon: "Users",
    roles: ["super_admin", "admin", "sales"],
    items: [
      { label: "Customer List", to: "/admin/customers", roles: ["super_admin", "admin", "sales"] },
      { label: "Purchase History", to: "/admin/customers/purchases", roles: ["super_admin", "admin", "sales"] },
      { label: "Wishlist", to: "/admin/customers/wishlists", roles: ["super_admin", "admin"] },
      { label: "Custom Design Requests", to: "/admin/enquiries?type=custom_design", roles: ["super_admin", "admin", "sales"] },
    ],
  },
  {
    group: "Enquiries",
    icon: "Mail",
    roles: ["super_admin", "admin", "sales", "team_manager"],
    items: [
      { label: "Contact Form Enquiries", to: "/admin/enquiries?type=contact", roles: ["super_admin", "admin", "sales", "team_manager"] },
      { label: "Custom Design Requests", to: "/admin/enquiries?type=custom_design", roles: ["super_admin", "admin", "sales", "team_manager"] },
      { label: "General Quote Requests", to: "/admin/enquiries?type=quote", roles: ["super_admin", "admin", "sales"] },
    ],
  },
  {
    group: "CRM",
    icon: "Users",
    roles: ["super_admin", "admin", "team_manager", "sales"],
    items: [
      { label: "Leads", to: "/admin/leads", roles: ["super_admin", "admin", "team_manager", "sales"] },
      { label: "All Clients", to: "/admin/clients", roles: ["super_admin", "admin", "team_manager", "sales"] },
    ],
  },
  {
    group: "Projects",
    icon: "ShoppingBag",
    roles: ["super_admin", "admin", "team_manager", "architect"],
    items: [
      { label: "All Projects", to: "/admin/projects", roles: ["super_admin", "admin", "team_manager", "architect"] },
      { label: "Active", to: "/admin/projects?tab=active", roles: ["super_admin", "admin", "team_manager", "architect"] },
      { label: "Due Today", to: "/admin/projects?tab=due_today", roles: ["super_admin", "admin", "team_manager", "architect"] },
      { label: "Overdue", to: "/admin/projects?tab=overdue", roles: ["super_admin", "admin", "team_manager", "architect"] },
    ],
  },
  {
    group: "Team Management",
    icon: "UserCog",
    roles: ["super_admin", "admin"],
    items: [
      { label: "Users", to: "/admin/users", roles: ["super_admin", "admin"] },
      { label: "Roles & Permissions", to: "/admin/roles", roles: ["super_admin"] },
      { label: "Activity Log", to: "/admin/activity", roles: ["super_admin", "admin"] },
    ],
  },
  {
    group: "Content Management",
    icon: "FileText",
    roles: ["super_admin", "admin", "team_manager", "content_writer"],
    items: [
      { label: "Blog", to: "/admin/blog", roles: ["super_admin", "admin", "team_manager", "content_writer"] },
      { label: "SEO Landing Pages", to: "/admin/landing", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Testimonials", to: "/admin/testimonials", roles: ["super_admin", "admin", "content_writer"] },
      { label: "FAQs", to: "/admin/faqs", roles: ["super_admin", "admin", "content_writer"] },
      { label: "Featured Plans", to: "/admin/featured", roles: ["super_admin", "admin", "team_manager"] },
    ],
  },
  {
    group: "Media Library",
    icon: "ImageIcon",
    roles: ["super_admin", "admin", "team_manager"],
    items: [
      { label: "Images", to: "/admin/media/images", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Floor Plans", to: "/admin/media/floor-plans", roles: ["super_admin", "admin", "team_manager"] },
      { label: "PDFs", to: "/admin/media/pdfs", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Videos", to: "/admin/media/videos", roles: ["super_admin", "admin", "team_manager"] },
      { label: "Documents", to: "/admin/media/documents", roles: ["super_admin", "admin", "team_manager"] },
    ],
  },
  {
    group: "Settings",
    icon: "Settings",
    roles: ["super_admin", "admin"],
    items: [
      { label: "Company Information", to: "/admin/settings?section=company", roles: ["super_admin", "admin"] },
      { label: "Branding & Assets", to: "/admin/branding", roles: ["super_admin", "admin"] },
      { label: "Homepage Content", to: "/admin/homepage", roles: ["super_admin", "admin"] },
      { label: "Contact Details", to: "/admin/settings?section=contact", roles: ["super_admin", "admin"] },
      { label: "Social Media", to: "/admin/settings?section=social", roles: ["super_admin", "admin"] },
      { label: "Email Settings", to: "/admin/settings?section=email", roles: ["super_admin", "admin"] },
      { label: "Currency", to: "/admin/currency", roles: ["super_admin", "admin"] },
      { label: "Taxes", to: "/admin/settings?section=tax", roles: ["super_admin", "admin"] },
      { label: "SEO", to: "/admin/settings?section=seo", roles: ["super_admin", "admin", "content_writer"] },
      { label: "Analytics", to: "/admin/settings?section=analytics", roles: ["super_admin", "admin"] },
      { label: "Integrations", to: "/admin/settings?section=integrations", roles: ["super_admin"] },
    ],
  },
  {
    group: "Reports",
    icon: "BarChart3",
    roles: ["super_admin", "admin"],
    items: [
      { label: "Sales Reports", to: "/admin/reports/sales", roles: ["super_admin", "admin"] },
      { label: "Revenue Reports", to: "/admin/reports/revenue", roles: ["super_admin", "admin"] },
      { label: "Popular Plans", to: "/admin/reports/popular", roles: ["super_admin", "admin"] },
      { label: "Customer Reports", to: "/admin/reports/customers", roles: ["super_admin", "admin"] },
      { label: "Download Reports", to: "/admin/reports/downloads", roles: ["super_admin", "admin"] },
    ],
  },
  {
    group: "Backup & Maintenance",
    icon: "Database",
    roles: ["super_admin"],
    items: [
      { label: "Database Backup", to: "/admin/backup", roles: ["super_admin"] },
      { label: "Restore", to: "/admin/restore", roles: ["super_admin"] },
      { label: "System Logs", to: "/admin/logs", roles: ["super_admin"] },
    ],
  },
];

export const canSee = (user, allowedRoles) => {
  if (!user) return false;
  if (allowedRoles?.includes("*")) return user.role !== "customer";
  return allowedRoles?.includes(user.role);
};

export const filteredNav = (user) => {
  if (!user) return [];
  return ADMIN_NAV
    .filter((g) => canSee(user, g.roles))
    .map((g) => ({ ...g, items: g.items.filter((it) => canSee(user, it.roles)) }))
    .filter((g) => g.items.length > 0);
};
